import { QualificationQuestion, QualificationSection } from '../lib/api';

// -- Local state types (with temp UUIDs before save) --

export interface LocalQuestion {
  _key: string;
  id?: number;
  question_text: string;
  question_type: QualificationQuestion['question_type'];
  help_text: string;
  is_required: boolean;
  is_scored: boolean;
  max_score: number;
  scoring_criteria: Record<string, unknown>;
  choices: string[];
}

export interface LocalSection {
  _key: string;
  id?: number;
  title: string;
  description: string;
  weight: number;
  is_required: boolean;
  questions: LocalQuestion[];
}

export const TEMPLATE_TYPES: Array<{ value: string; label: string; description: string }> = [
  { value: 'onboarding', label: 'Onboarding Questionnaire', description: 'Required during supplier onboarding process' },
  { value: 'standard', label: 'Standard Questionnaire', description: 'General purpose operational questionnaire' },
  { value: 'compliance', label: 'Compliance Questionnaire', description: 'Regulatory and compliance assessments' },
  { value: 'audit', label: 'Audit Questionnaire', description: 'Supplier audit and verification' },
  { value: 'esg', label: 'ESG Questionnaire', description: 'Environmental, Social, and Governance' },
  { value: 'quality', label: 'Quality Questionnaire', description: 'Quality management assessments' },
  { value: 'custom', label: 'Custom', description: 'Custom questionnaire type' },
];

export const RECURRENCE_FREQUENCIES: Array<{ value: string; label: string; description: string }> = [
  { value: 'one_time', label: 'One-time only', description: 'Questionnaire is issued once' },
  { value: 'monthly', label: 'Monthly', description: 'Reissued every month' },
  { value: 'quarterly', label: 'Quarterly', description: 'Reissued every 3 months' },
  { value: 'semi_annual', label: 'Every 6 Months', description: 'Reissued twice a year' },
  { value: 'annual', label: 'Annual', description: 'Reissued once a year' },
  { value: 'biennial', label: 'Every 2 Years', description: 'Reissued every 2 years' },
  { value: 'custom', label: 'Custom Interval', description: 'Specify custom interval in days' },
];

export const QUESTION_TYPES: Array<{ value: QualificationQuestion['question_type']; label: string }> = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'rating', label: 'Rating' },
];

export const SECTION_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', ring: 'ring-blue-200' },
  { bg: 'bg-violet-50', border: 'border-violet-400', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700', ring: 'ring-violet-200' },
  { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-200' },
  { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', ring: 'ring-amber-200' },
  { bg: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', ring: 'ring-rose-200' },
  { bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700', ring: 'ring-cyan-200' },
];

let keyCounter = 0;
export function genKey(): string {
  keyCounter += 1;
  return `_tmp_${Date.now()}_${keyCounter}`;
}

export function makeQuestion(): LocalQuestion {
  return {
    _key: genKey(),
    question_text: '',
    question_type: 'text',
    help_text: '',
    is_required: true,
    is_scored: true,
    max_score: 10,
    scoring_criteria: {},
    choices: [],
  };
}

export function makeSection(): LocalSection {
  return {
    _key: genKey(),
    title: '',
    description: '',
    weight: 0,
    is_required: true,
    questions: [makeQuestion()],
  };
}

export function sectionFromApi(s: QualificationSection): LocalSection {
  return {
    _key: genKey(),
    id: s.id,
    title: s.title,
    description: s.description || '',
    weight: s.weight,
    is_required: s.is_required,
    questions: (s.questions || [])
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((q) => ({
        _key: genKey(),
        id: q.id,
        question_text: q.question_text,
        question_type: q.question_type,
        help_text: q.help_text || '',
        is_required: q.is_required,
        is_scored: q.is_scored,
        max_score: q.max_score,
        scoring_criteria: q.scoring_criteria || {},
        choices: q.choices || [],
      })),
  };
}

export function getQuestionTypeLabel(type: string): string {
  return QUESTION_TYPES.find((t) => t.value === type)?.label ?? type;
}

// CR-5918: Single/Multiple Choice questions must have at least 2 non-empty
// answer options before save/publish, otherwise suppliers receive mandatory
// questions with no possible answers.
export const MIN_CHOICES_PER_QUESTION = 2;

export interface ChoiceQuestionError {
  /** _key of the offending question, used to surface the error inline on that question. */
  questionKey: string;
  sectionIdx: number;
  questionIdx: number;
  /** Human-readable, location-prefixed message for the summary banner. */
  message: string;
}

/**
 * Identify Single/Multiple Choice questions with fewer than the required number
 * of non-empty answer options. Returns one structured error per offending
 * question so the caller can render both a summary banner and an inline message
 * pinned to the exact question (CR-5918 rework: feedback must be visible and
 * persistent, not a transient toast).
 */
export function getChoiceQuestionErrors(sections: LocalSection[]): ChoiceQuestionError[] {
  const errors: ChoiceQuestionError[] = [];
  sections.forEach((section, sIdx) => {
    section.questions.forEach((q, qIdx) => {
      if (q.question_type === 'single_choice' || q.question_type === 'multiple_choice') {
        const valid = q.choices.filter((c) => c.trim().length > 0);
        if (valid.length < MIN_CHOICES_PER_QUESTION) {
          const label = q.question_type === 'single_choice' ? 'Single Choice' : 'Multiple Choice';
          errors.push({
            questionKey: q._key,
            sectionIdx: sIdx,
            questionIdx: qIdx,
            message: `Section ${sIdx + 1} · Q${qIdx + 1} (${label}): add at least ${MIN_CHOICES_PER_QUESTION} answer options`,
          });
        }
      }
    });
  });
  return errors;
}

/** Backwards-compatible string[] view of {@link getChoiceQuestionErrors}. */
export function validateChoiceQuestions(sections: LocalSection[]): string[] {
  return getChoiceQuestionErrors(sections).map((e) => e.message);
}
